import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import { createApi } from '@reduxjs/toolkit/query/react';

/** A single selectable option for enumeration / select fields. */
export interface PropertyOption {
  label: string;
  value: string;
  displayOrder?: number;
  hidden?: boolean;
}

/** One field the user must fill in to enter the target stage. */
export interface RequirementField {
  /** HubSpot property name — the key sent back in `properties`. */
  property: string;
  label: string;
  /** string | enumeration | date | datetime | number | bool */
  type: string;
  /** text | textarea | select | checkbox | date | number | booleancheckbox ... */
  fieldType: string;
  /** Non-empty for enumeration / select fields. */
  options: PropertyOption[];
  required: boolean;
  /** Value already on the deal ('' is normalised to null by the backend). */
  currentValue: string | null;
  /** Which rule(s) surfaced this field (debug only). */
  sourceRuleIds: number[];
}

export interface TransitionRequirementsResponse {
  dealId: string;
  currentStageId: string | null;
  targetStageId: string;
  targetStageLabel: string | null;
  /** false = every required field is already set; commit can happen immediately. */
  needsInput: boolean;
  fields: RequirementField[];
}

export interface CommitTransitionDto {
  targetStageId: string;
  /**
   * Stage the deal is believed to be in NOW (use requirements.currentStageId).
   * A mismatch yields a 409 — re-fetch requirements and retry.
   */
  expectedCurrentStageId: string;
  /** ONLY the fields the user actually filled in. Never send '' (clears the property). */
  properties: Record<string, unknown>;
}

export interface HubspotDealRecord {
  id: string;
  properties: Record<string, string | null>;
}

/** 422 payload — map each entry to its field and show inline. */
export interface TransitionFieldError {
  property: string;
  message: string;
}

/** Result of uploading / resolving a file for a `fieldType: 'file'` property. */
export interface DealAttachmentUploadResult {
  /** File Manager id — the value stored on the HubSpot property. */
  reference: string;
  fileName: string;
  /** Short-lived signed URL for immediate preview (~60 min). Fetch lazily. */
  webViewLink?: string;
  /** Reserved, currently unused. */
  documentId?: string;
}

export const stageTransitionApi = createApi({
  reducerPath: 'stageTransitionApi',
  baseQuery: customBaseQuery,
  endpoints: (build) => ({
    getTransitionRequirements: build.query<
      TransitionRequirementsResponse,
      { dealId: string; targetStageId: string }
    >({
      query: ({ dealId, targetStageId }) =>
        `/v1/deals/${dealId}/transitions/${targetStageId}/requirements`,
      transformResponse: (r: ApiResponse<TransitionRequirementsResponse>) =>
        r.data,
    }),
    commitTransition: build.mutation<
      HubspotDealRecord,
      { dealId: string; body: CommitTransitionDto }
    >({
      query: ({ dealId, body }) => ({
        url: `/v1/deals/${dealId}/transitions`,
        method: 'POST',
        body,
      }),
      transformResponse: (r: ApiResponse<HubspotDealRecord>) => r.data,
    }),
    // Upload a file for a `fieldType: 'file'` requirement property. Returns the
    // File Manager id (`reference`) to store on the property at commit time.
    uploadDealAttachment: build.mutation<
      DealAttachmentUploadResult,
      { dealId: string; property: string; file: File; folderPath?: string }
    >({
      query: ({ dealId, property, file, folderPath }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('property', property);
        if (folderPath) formData.append('folderPath', folderPath);
        return {
          url: `/v1/crm-orchestration/deals/${dealId}/attachments`,
          method: 'POST',
          body: formData, // browser sets the multipart boundary
        };
      },
      transformResponse: (r: ApiResponse<DealAttachmentUploadResult>) => r.data,
    }),
    // Resolve a file property's stored id to a signed URL (prefill / read-back).
    getDealAttachment: build.query<
      DealAttachmentUploadResult | null,
      { dealId: string; property: string }
    >({
      query: ({ dealId, property }) =>
        `/v1/crm-orchestration/deals/${dealId}/attachments/${property}`,
      transformResponse: (r: ApiResponse<DealAttachmentUploadResult | null>) =>
        r.data,
    }),
  }),
});

export const {
  useGetTransitionRequirementsQuery,
  useCommitTransitionMutation,
  useUploadDealAttachmentMutation,
  useGetDealAttachmentQuery,
} = stageTransitionApi;
